import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ProfileHeader,
  InfoCard,
  NotificationPreferences,
  SecuritySettings,
  SettingsSection,
  LogoutButton,
} from './components';
import { ChangePasswordModal } from '../../../components/ChangePasswordModal';
import {
  CallCenterProfile as CallCenterProfileType,
  NotificationPreference,
  defaultNotificationPreferences,
  defaultSecurityOptions,
  defaultSettingsItems,
} from './profileTypes';
import { useAuth, isUserActive } from '../../../contexts/AuthContext';
import { getUserPreferences, saveUserPreferences } from '../../../services/accountApi';
import styles from './CallCenterProfile.module.css';

/**
 * Persisted-locally settings used by this page. Backend has no
 * /user-preferences endpoint yet, so the call center role keeps these in
 * localStorage so toggles, language, etc. survive a refresh. Once the
 * backend ships a preferences resource, swap the load/save lines below.
 */
const PREFS_KEY = 'vinpulse_callcenter_prefs';

interface PersistedPrefs {
  notifications: NotificationPreference[];
  language: string;
  offlineMode: boolean;
  general: {
    soundsEnabled: boolean;
    compactDensity: boolean;
  };
}

const DEFAULT_PREFS: PersistedPrefs = {
  notifications: defaultNotificationPreferences,
  language: 'en-US',
  offlineMode: false,
  general: { soundsEnabled: true, compactDensity: false },
};

function loadPrefs(): PersistedPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PREFS,
      ...parsed,
      general: { ...DEFAULT_PREFS.general, ...(parsed.general ?? {}) },
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function savePrefs(p: PersistedPrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // ignore — private mode / quota
  }
}

const CallCenterProfile: React.FC = () => {
  const { user } = useAuth();
  // Hydrate from localStorage immediately so the UI doesn't flash defaults
  // on first paint; the backend value (fetched on mount) takes precedence
  // once it lands. localStorage continues to act as an offline cache.
  const [prefs, setPrefs] = useState<PersistedPrefs>(() => loadPrefs());
  const [openSetting, setOpenSetting] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Track whether we've finished the initial server load. We avoid writing
  // back to the server during hydration to prevent overwriting a richer
  // server-side blob with the local default.
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef<number | null>(null);

  // 1) Hydrate from server on mount. If the call fails (offline / unauth)
  //    we keep whatever loadPrefs() returned from localStorage.
  useEffect(() => {
    let cancelled = false;
    getUserPreferences()
      .then((res) => {
        if (cancelled) return;
        if (res?.prefs && typeof res.prefs === 'object' && Object.keys(res.prefs).length > 0) {
          const merged: PersistedPrefs = {
            ...DEFAULT_PREFS,
            ...(res.prefs as Partial<PersistedPrefs>),
            general: { ...DEFAULT_PREFS.general, ...(((res.prefs as any).general) ?? {}) },
          };
          setPrefs(merged);
          savePrefs(merged); // refresh localStorage cache
        }
      })
      .catch(() => { /* silent — localStorage cache is good enough */ })
      .finally(() => { hydratedRef.current = true; });
    return () => { cancelled = true; };
  }, []);

  // 2) Persist whenever prefs change — local cache + debounced server PUT.
  //    The 800ms debounce avoids hammering the API on multi-toggle flicks
  //    (e.g. switching three notifications in quick succession).
  useEffect(() => {
    savePrefs(prefs);
    if (!hydratedRef.current) return;
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveUserPreferences(prefs as unknown as Record<string, unknown>).catch(() => undefined);
    }, 800);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
  }, [prefs]);

  const handleTogglePreference = useCallback((id: string) => {
    setPrefs((p) => ({
      ...p,
      notifications: p.notifications.map((n) =>
        n.id === id ? { ...n, enabled: !n.enabled } : n
      ),
    }));
  }, []);

  const handleSettingClick = useCallback((id: string) => {
    setOpenSetting((cur) => (cur === id ? null : id));
  }, []);

  const handleSecurityClick = useCallback((id: string) => {
    if (id === 'change-password') setShowPasswordModal(true);
  }, []);

  const flashSaved = useCallback(() => {
    setSavedFlash(true);
    window.setTimeout(() => setSavedFlash(false), 1600);
  }, []);

  const profile: CallCenterProfileType = useMemo(() => ({
    id: user?.id ?? 'N/A',
    staffName: user?.full_name ?? 'N/A',
    role: user?.role ?? 'Call Center',
    status: isUserActive(user) ? 'Active' : 'Inactive',
    queue: 'N/A',
    staffId: user?.employee_id || 'N/A',
  }), [user]);

  // Reflect the live language/offline-mode values in the settings rows so
  // the user sees what they have selected without expanding.
  const settingsItems = useMemo(() => defaultSettingsItems.map((it) => {
    if (it.id === 'language') {
      const label = prefs.language === 'en-US' ? 'English (US)'
        : prefs.language === 'es-ES' ? 'Spanish'
        : prefs.language === 'fr-FR' ? 'French'
        : prefs.language;
      return { ...it, subtitle: label };
    }
    if (it.id === 'offline-mode') {
      return { ...it, subtitle: prefs.offlineMode ? 'Enabled — sync when online' : 'Disabled' };
    }
    if (it.id === 'notifications') {
      const enabled = prefs.notifications.filter((n) => n.enabled).length;
      return { ...it, subtitle: `${enabled} of ${prefs.notifications.length} types enabled` };
    }
    return it;
  }), [prefs]);

  // All fields show the admin-configured value from /users/me (migration 027);
  // any column that's still NULL renders 'N/A'.
  const personalFields = [
    { label: 'Staff Name', value: profile.staffName },
    { label: 'Employee ID', value: profile.staffId },
    { label: 'Assigned Queue', value: profile.queue, withChevron: true },
  ];

  const contactFields = [
    { label: 'Email', value: user?.email ?? 'N/A' },
    { label: 'Phone', value: user?.phone || 'N/A' },
    { label: 'Department', value: user?.department || 'N/A' },
  ];

  return (
    <div className={styles.container}>
      <ProfileHeader profile={profile} />

      <div className={styles.infoGrid}>
        <InfoCard title="Personal Information" icon="personal" fields={personalFields} />
        <InfoCard title="Contact Information" icon="contact" fields={contactFields} />
      </div>

      <NotificationPreferences
        preferences={prefs.notifications}
        onToggle={(id) => { handleTogglePreference(id); flashSaved(); }}
      />
      <SecuritySettings options={defaultSecurityOptions} onOptionClick={handleSecurityClick} />

      <SettingsSection items={settingsItems} onItemClick={handleSettingClick} />

      {openSetting === 'notifications' && (
        <SettingsPanel title="Notification Preferences">
          <p className={styles.panelHint}>
            Use the toggles in the section above to enable or disable each notification type.
            Changes are saved automatically.
          </p>
        </SettingsPanel>
      )}
      {openSetting === 'language' && (
        <SettingsPanel title="Language">
          <div className={styles.panelRow}>
            <label className={styles.panelLabel}>App Language</label>
            <select
              value={prefs.language}
              onChange={(e) => { setPrefs((p) => ({ ...p, language: e.target.value })); flashSaved(); }}
              className={styles.panelSelect}
            >
              <option value="en-US">English (US)</option>
              <option value="es-ES">Spanish</option>
              <option value="fr-FR">French</option>
            </select>
          </div>
        </SettingsPanel>
      )}
      {openSetting === 'offline-mode' && (
        <SettingsPanel title="Offline Mode">
          <label className={styles.panelToggleRow}>
            <input
              type="checkbox"
              checked={prefs.offlineMode}
              onChange={(e) => { setPrefs((p) => ({ ...p, offlineMode: e.target.checked })); flashSaved(); }}
            />
            <span>
              <strong>Enable offline mode</strong>
              <small>When enabled, changes you make are queued and pushed when you reconnect.</small>
            </span>
          </label>
        </SettingsPanel>
      )}
      {openSetting === 'general' && (
        <SettingsPanel title="General Settings">
          <label className={styles.panelToggleRow}>
            <input
              type="checkbox"
              checked={prefs.general.soundsEnabled}
              onChange={(e) => {
                setPrefs((p) => ({ ...p, general: { ...p.general, soundsEnabled: e.target.checked } }));
                flashSaved();
              }}
            />
            <span>
              <strong>Sound notifications</strong>
              <small>Play a chime for new tickets and replies.</small>
            </span>
          </label>
          <label className={styles.panelToggleRow}>
            <input
              type="checkbox"
              checked={prefs.general.compactDensity}
              onChange={(e) => {
                setPrefs((p) => ({ ...p, general: { ...p.general, compactDensity: e.target.checked } }));
                flashSaved();
              }}
            />
            <span>
              <strong>Compact density</strong>
              <small>Show more rows per screen in tables.</small>
            </span>
          </label>
        </SettingsPanel>
      )}

      {savedFlash && <div className={styles.savedFlash}>Saved</div>}

      <LogoutButton />

      <p className={styles.version}>VIN pulse OS v2.1.0</p>

      <ChangePasswordModal
        open={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
};

const SettingsPanel: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className={styles.settingsPanel}>
    <h3 className={styles.panelTitle}>{title}</h3>
    {children}
  </div>
);

export const CallCenterProfilePage: React.FC = () => {
  return <CallCenterProfile />;
};

export { CallCenterProfile };
export default CallCenterProfile;
